import React, { useCallback } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/loading-button';
import Tooltip from '@atlaskit/tooltip';
import { SpotlightTarget } from '@atlaskit/onboarding';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { expValEquals } from '@confluence/feature-experiments';
import { ShortcutVisualizer } from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import { EDITOR2_SHORTCUTS } from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import {
	EDITOR_ONBOARDING_STATE_KEYS,
	ONBOARDING_EXPERIENCES,
	CONTEXTUAL_INVITE_NUDGE,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useOnboardingEditorActions } from '@confluence/onboarding-editor/entry-points/useOnboardingEditorState';

// eslint-disable-next-line import/no-cycle
import { displayPublishErrorFlag } from './publishHelpers';
// eslint-disable-next-line import/no-cycle
import { WideTooltip, TooltipText } from './PublishButtonWithMoreOptions';
import { preloadPublishModal } from './preloadPublishModal';
import { i18nShared } from './i18nShared';
import { usePublishModalActions } from './usePublishModalActions';

const i18n = defineMessages({
	publishButtonTooltip: {
		id: 'publish-modal.default-publish-button-tooltip',
		description:
			'Tooltip for publish button (or split button) to inform user that they can change restrictions and location of the page',
		defaultMessage: 'Select and confirm publish settings',
	},
});

export type Props = {
	saveDraft?: () => void;
	openModalWithAnalytics: (string) => () => void;
	savingInProgress: boolean;
	publishButtonDisabled: boolean | undefined;
	title: string | null;
	flags: FlagsStateContainer;
};

export const DefaultPublishModalButton = ({
	saveDraft,
	openModalWithAnalytics,
	savingInProgress,
	publishButtonDisabled,
	title,
	flags,
}: Props) => {
	const intl = useIntl();
	const { data } = useGetOnboardingState([
		...Object.values(EDITOR_ONBOARDING_STATE_KEYS),
		ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
	]);
	const { setOnboardingState } = useSetOnboardingState();
	const { setHasMetPublishNudgeConditions } = useOnboardingEditorActions();
	const onboardingState = deserializeState(data);
	const isLivePage = useIsCurrentPageLive();

	/**
	 * this is experimental logic for a users very first page publish, indstead of showing the publish modal
	 * directly publish the page and then show them the improved invite page if they are part of the experiment
	 * else do the default experience of opening the modal.
	 * then open the invite modal which is triggered as part of the usePublish hook
	 */
	const { handlePublish } = usePublishModalActions();
	const publishButtonOnClick = useCallback(() => {
		if (onboardingState.isFoundingAdmin && !onboardingState?.hasUserPublished) {
			if (expValEquals('cfind-4177-improve-contextual-invite-component', 'isTreatment', true)) {
				if (!title) {
					void displayPublishErrorFlag({
						validationMessage: 'TITLE_IS_BLANK',
						isLivePage,
						flags,
						isTitleEmpty: true,
						isAIEnabledForContent: true,
						formatMessage: intl.formatMessage,
						publish: () => {
							return handlePublish({
								analyticsSource: 'KeyboardShortcut',
								isUnpublishedDraft: true,
							}).then(() => {
								setHasMetPublishNudgeConditions(true);
								void setOnboardingState({
									key: CONTEXTUAL_INVITE_NUDGE.EXPERIMENT_ELIGIBLE_TAG,
									value: 'true',
								});
							});
						},
					});
				} else {
					void handlePublish({
						analyticsSource: 'KeyboardShortcut',
						isUnpublishedDraft: true,
					}).then(() => {
						setHasMetPublishNudgeConditions(true);
						void setOnboardingState({
							key: CONTEXTUAL_INVITE_NUDGE.EXPERIMENT_ELIGIBLE_TAG,
							value: 'true',
						});
					});
				}
			} else {
				openModalWithAnalytics('PublishModalPublishMoreActionsButton')();
			}
		} else {
			openModalWithAnalytics('PublishModalPublishMoreActionsButton')();
		}
	}, [
		isLivePage,
		flags,
		intl.formatMessage,
		title,
		onboardingState.hasUserPublished,
		onboardingState.isFoundingAdmin,
		handlePublish,
		setOnboardingState,
		openModalWithAnalytics,
		setHasMetPublishNudgeConditions,
	]);

	return (
		<Tooltip
			component={WideTooltip}
			content={
				<TooltipText>
					<FormattedMessage {...i18n.publishButtonTooltip} />
					<ShortcutVisualizer shortcut={EDITOR2_SHORTCUTS.PUBLISH} isEditorShortcut isTooltip />
				</TooltipText>
			}
		>
			<SpotlightTarget name="publish-button">
				<Button
					appearance="primary"
					onMouseDown={() => preloadPublishModal(saveDraft)}
					onMouseEnter={() => preloadPublishModal(saveDraft)}
					onFocus={() => preloadPublishModal(saveDraft)}
					onClick={() => {
						publishButtonOnClick();
					}}
					// This test ID is used in tests and in experience tracking.  Be sure to update all occurences before changing it.
					testId="publish-button"
					isLoading={savingInProgress}
					isDisabled={publishButtonDisabled}
				>
					<FormattedMessage {...i18nShared.publishButton} />
				</Button>
			</SpotlightTarget>
		</Tooltip>
	);
};
